import { c as _c } from "react/compiler-runtime";
import { memo, ReactElement, useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useExternalFilterBridge } from '../../hooks/useExternalFilterBridge';
import { FormToDataGridConnectorProps } from '../../types/FormToDataGridConnectorProps';
export type ExternalFiltersPortalProps = {
  externalFilterId: string;
  filterComponent: (props: FormToDataGridConnectorProps) => ReactElement;
};

/**
 * Renders a `filterComponent` into an external DOM element via React portal.
 *
 * Delegates store↔filter bridging to `useExternalFilterBridge`.
 * The portal mechanism uses `useLayoutEffect` + state for DOM element lookup
 * to avoid reads during render (React Compiler safe).
 *
 * ## Performance
 *
 * - `memo()` prevents re-renders from unrelated store changes propagating through EagGridTable.
 * - Store bridging is handled by `useExternalFilterBridge` (memoized values + stable onChange).
 * - DOM element lookup runs once per `externalFilterId` change.
 */
export const ExternalFiltersPortal = memo(props => {
  const $ = _c(10);
  const {
    values,
    onChange
  } = useExternalFilterBridge();
  const [portalTarget, setPortalTarget] = useState(null);
  let t0;
  let t1;
  if ($[0] !== props.externalFilterId) {
    t0 = () => {
      setPortalTarget(document.getElementById(props.externalFilterId));
    };
    t1 = [props.externalFilterId];
    $[0] = props.externalFilterId;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useLayoutEffect(t0, t1);
  const FilterComponent = props.filterComponent;
  if (!portalTarget) {
    return null;
  }
  let t2;
  if ($[3] !== FilterComponent || $[4] !== onChange || $[5] !== values) {
    t2 = <FilterComponent values={values} onChange={onChange} />;
    $[3] = FilterComponent;
    $[4] = onChange;
    $[5] = values;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== portalTarget || $[8] !== t2) {
    t3 = createPortal(t2, portalTarget);
    $[7] = portalTarget;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return t3;
});